if (!customElements.get('product-compare-panel')) {
    class ProductComparePanel extends HTMLElement {
        constructor() {
            super();
            this.toggle = this.querySelector('.compare-toggle');
            this.drawer = this.querySelector('.compare-drawer');
            this.clear = this.querySelector('.compare-clear');
            this.start = this.querySelector('.compare-start');
            this.modal = this.querySelector('.product-compare-modal');
            this.table = this.querySelector('product-compare-table');
            this.max = Number(this.dataset.max);
        }

        connectedCallback() {
            setTimeout(() => this.toggle.classList.add('compare-toggle--active'), 250);
            this.addEventListeners();
            this.updateCompareCheckboxes();
            this.updateCompareCounters();
            this.updateStartButton();
        }

        addEventListeners() {
            this.drawer.addEventListener('change', (e) => {
                const select = e.target.closest('.product-compare-variant-picker--select');
                if (!select) return;
                const btn = select.nextElementSibling;
                if (btn) btn.dataset.variantId = select.value;
            });

            this.drawer.addEventListener('click', async (e) => {
                const btn = e.target.closest('.product-compare-variant-picker--add-to-cart');
                if (!btn) return;
                e.preventDefault();
                const variantId = btn.dataset.variantId;
                if (!variantId) return;
                const label = btn.dataset.label || 'Add to cart';
                const span = btn.querySelector('span');
                btn.disabled = true;
                span.textContent = 'Adding...';
                try {
                    const res = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
                    });
                    if (res.ok) {
                        span.textContent = 'Added!';
                        document.dispatchEvent(new CustomEvent('cart:refresh'));
                    } else {
                        span.textContent = 'Error — try again';
                    }
                } catch {
                    span.textContent = 'Error — try again';
                } finally {
                    setTimeout(() => { btn.disabled = false; span.textContent = label; }, 2000);
                }
            });

            this.toggle.addEventListener('click', this.onButtonClick.bind(this));
            this.drawer.addEventListener('click', this.onRemoveClick.bind(this));
            this.clear.addEventListener('click', this.onClearClick.bind(this));
            this.start.addEventListener('click', this.onStartClick.bind(this));
            document.addEventListener('change', this.onCheckboxClick.bind(this));
        }

        getSelectedProducts() {
            return JSON.parse(localStorage.getItem('compare-products-vision')) || [];
        }

        addToCompare(productId, productUrl, productHandle) {
            const compareProducts = this.getSelectedProducts();
            if (!compareProducts.some(p => p.id === productId)) {
                compareProducts.push({ id: productId, url: productUrl, handle: productHandle });
                localStorage.setItem('compare-products-vision', JSON.stringify(compareProducts));
                this.updateCompareComponents();
            }
        }

        removeFromCompare(productId) {
            const compareProducts = this.getSelectedProducts().filter(p => p.id !== productId);
            localStorage.setItem('compare-products-vision', JSON.stringify(compareProducts));
            this.updateCompareComponents();
        }

        updateStartButton() {
            this.start.disabled = !this.getCompareCount();
        }

        onCheckboxClick(event) {
            if (!event.target.classList.contains('product-card-compare--checkbox')) return;
            const { productId, productUrl, productHandle } = event.target.dataset;
            if (event.target.checked) {
                if (this.getCompareCount() < this.max) {
                    this.addToCompare(productId, productUrl, productHandle);
                } else {
                    const oldest = this.getSelectedProducts()[0];
                    this.removeFromCompare(oldest.id);
                    this.addToCompare(productId, productUrl, productHandle);
                }
            } else {
                this.removeFromCompare(productId);
            }
        }

        updateCompareCheckboxes() {
            const compareProducts = this.getSelectedProducts();
            document.querySelectorAll('.product-card-compare--checkbox').forEach(checkbox => {
                checkbox.checked = compareProducts.some(p => p.id === checkbox.dataset.productId);
                checkbox.disabled = false;
            });
        }

        onRemoveClick(event) {
            if (!event.target.classList.contains('compare-remove')) return;
            this.removeFromCompare(event.target.dataset.productId);
            event.target.closest('product-card-small')?.remove();
        }

        onClearClick() {
            this.getSelectedProducts().forEach(p => this.removeFromCompare(p.id));
            this.drawer.querySelector('.compare-drawer--list').innerHTML =
                '<div class="compare-drawer--list-item"></div>'.repeat(this.max);
            this.closeDrawer();
        }

        onStartClick() {
            this.modal.show();
            this.table.dispatchEvent(new CustomEvent('render-product-compare-modal', { bubbles: true }));
        }

        openDrawer() {
            this.drawer.removeAttribute('inert');
            this.drawer.focus();
            this.drawer.classList.add('active');
            document.body.classList.add('open-cc');
        }

        closeDrawer() {
            this.drawer.setAttribute('inert', '');
            this.drawer.blur();
            this.drawer.classList.remove('active');
            document.body.classList.remove('open-cc');
        }

        onButtonClick() {
            this.renderProducts();
            this.openDrawer();
        }

        getCompareCount() {
            return this.getSelectedProducts().length;
        }

        updateCompareCounters() {
            const count = this.getCompareCount();
            document.querySelectorAll('.compare-counter').forEach(counter => {
                counter.textContent = count;
            });
        }

        async renderProducts() {
            const compareProducts = this.getSelectedProducts();
            const cards = await Promise.all(compareProducts.map(async (product) => {
                try {
                    const res = await fetch(`/products/${product.handle}.js`);
                    const data = await res.json();
                    return this.buildCardHTML(data, product.id);
                } catch {
                    return '';
                }
            }));
            const html = cards.join('') +
                '<div class="compare-drawer--list-item"></div>'.repeat(this.max - compareProducts.length);
            this.drawer.querySelector('.compare-drawer--list').innerHTML = html;
        }

        buildCardHTML(product, compareId) {
            const variant = product.variants.find(v => v.available) || product.variants[0];
            const img = product.featured_image
                ? `<img src="${product.featured_image}" alt="${this.esc(product.title)}" width="70" height="80">`
                : '';
            const price = this.formatMoney(variant.price);
            const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
            const priceHTML = hasCompare
                ? `<s class="price--compare">${this.formatMoney(variant.compare_at_price)}</s> <span class="price--sale">${price}</span>`
                : `<span>${price}</span>`;
            const options = product.variants.map(v =>
                `<option value="${v.id}"${v.id === variant.id ? ' selected' : ''}${!v.available ? ' disabled' : ''}>${this.esc(v.title)}${!v.available ? ' – Sold out' : ''}</option>`
            ).join('');
            const select = product.variants.length > 1
                ? `<select class="product-compare-variant-picker--select" data-product-id="${product.id}">${options}</select>`
                : '';
            return `<product-card-small class="product-card-small" data-url="${product.url}" data-id="${variant.id}" data-product-handle="${product.handle}">
                <div class="product-card-small--image">
                    <a href="${product.url}" class="product-cart-small--image-link">${img}</a>
                </div>
                <div class="product-card-small--info">
                    <a href="${product.url}" class="product-card-small--link">${this.esc(product.title)}</a>
                    <div class="product-card-small--add-to-cart">
                        <div class="price">${priceHTML}</div>
                    </div>
                    ${select}
                    <button class="product-compare-variant-picker--add-to-cart button accent" type="button" data-variant-id="${variant.id}" data-label="Add to cart"${!product.available ? ' disabled' : ''}>
                        <span>Add to cart</span>
                    </button>
                    <button class="compare-remove" data-product-id="${compareId}">✕</button>
                </div>
            </product-card-small>`;
        }

        formatMoney(cents) {
            return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }

        esc(str) {
            return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        }

        updateCompareComponents() {
            this.updateCompareCheckboxes();
            this.updateCompareCounters();
            this.updateStartButton();
            if (this.drawer.classList.contains('active')) {
                this.renderProducts();
            }
        }

        getSectionInnerHTML(html, selector) {
            return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
        }
    }

    customElements.define('product-compare-panel', ProductComparePanel);
}